/**
 * "Notifications" block of a row kebab (#86) — rendered from both the profile and the group menu,
 * so the two cannot drift apart.
 *
 * One `role="menuitemcheckbox"` item per mute type, checked when the type still fires. The two
 * viral flavors read as a pair: "Viral Posts" is the whole gate, "Viral Posts (watch checks)"
 * silences only the alerts a watch check triggers, leaving scheduled runs loud.
 * Every click writes immediately (no draft, no Save) and the menu deliberately **stays open** — the
 * `ProfileGroupsMenuItems` precedent: three independent checkboxes usually get toggled in one visit.
 *
 * The boxes show the subject's *own* state; a profile muted through one of its groups still reads
 * checked here, exactly as the Mode cell shows the profile's own mode.
 */

import type { NotificationMutesBody, NotificationsBlockDTO } from '@lw/shared';
import { MenuItem, MenuLabel } from '../../components';

export type NotificationsMenuItemsProps = {
  current: NotificationsBlockDTO;
  disabled?: boolean;
  onToggle: (patch: NotificationMutesBody) => void;
};

const ITEMS = [
  { key: 'viralPost', label: 'Viral Posts' },
  { key: 'viralPostWatch', label: 'Viral Posts (watch checks)' },
  { key: 'newPost', label: 'New Post' },
] as const;

export function NotificationsMenuItems({
  current,
  disabled = false,
  onToggle,
}: NotificationsMenuItemsProps) {
  return (
    <>
      <MenuLabel>Notifications</MenuLabel>
      {ITEMS.map((item) => {
        const checked = current[item.key];
        return (
          <MenuItem
            key={item.key}
            role="menuitemcheckbox"
            checked={checked}
            disabled={disabled}
            onClick={() => onToggle({ [item.key]: !checked })}
          >
            <span className="mode-check" aria-hidden="true">
              {checked ? '✓' : ''}
            </span>
            {item.label}
          </MenuItem>
        );
      })}
    </>
  );
}

export default NotificationsMenuItems;
